import {
  Component,
  OnInit,
  HostBinding,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { views } from "@onsip/web/app/phone/views";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ModalMaterialComponent } from "../../../shared/components/modal/modal-material.component";
import { take, map, filter } from "rxjs/operators";
import { Router } from "@angular/router";
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
  FormArray,
  NonNullableFormBuilder
} from "@angular/forms";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Recording } from "@onsip/common/services/api/resources/recording/recording";
import { SnackbarService } from "../../../shared/components/snackbar/snackbar.service";
import { MatStepper } from "@angular/material/stepper";
import { FormatSingularAddressTypePipe } from "../../../shared/pipes/format-singular-address-type.pipe";
import { AccountResourceService } from "@onsip/common/services/api/resources/package/account-resource.service";
import { PackageService } from "@onsip/common/services/api/resources/package/package.service";
import { MusicSource } from "@onsip/common/services/api/resources/musicSource/music-source";
import { MusicSourceService } from "@onsip/common/services/api/resources/musicSource/music-source.service";
import { EditMusicSourceDialogComponent } from "@onsip/web/features/shared/components/editMusicSourceDialog/edit-music-source-dialog.component";
import { AdvVoicemailbox } from "@onsip/common/services/api/resources/voicemail/adv-voicemailbox";
import { StorageServiceService } from "@onsip/common/services/api/resources/storageService/storage-service.service";
import { StorageService } from "@onsip/common/services/api/resources/storageService/storage-service";
import { AddStorageServiceDialogComponent } from "../addStorageServiceDialog/add-storage-service-dialog.component";
import { VoicemailService } from "@onsip/common/services/api/resources/voicemail/voicemail.service";
import { AdvQueueService } from "@onsip/common/services/api/resources/queue/adv-queue.service";
import { UserAddress } from "@onsip/common/services/api/resources/userAddress/user-address";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { AdvQueueEmailReportService } from "@onsip/common/services/api/resources/queue/adv-queue-email-report.service";
import { UserAddressService } from "@onsip/common/services/api/resources/userAddress/user-address.service";
import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import {
  ringStrategies,
  ringDurations,
  connectionDelays,
  wrapupTimes,
  announceFrequencies,
  callerTimeouts,
  reportFrequencies,
  frequencyMap,
  ringStrategyMapRev,
  recordingAnnouncement
} from "../helpers/acd-queues-dropdown-options";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

interface EmailReport {
  frequency: string;
  email: string;
}

@Component({
  selector: "onsip-acd-queues-setup",
  templateUrl: "./acd-queues-setup.component.html",
  styleUrls: ["./acd-queues-setup.component.scss"],
  providers: [
    FormatSingularAddressTypePipe,
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AcdQueuesSetupComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-admin-host-grid")
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;
  @ViewChild("stepper") stepper!: MatStepper;
  @ViewChild("queueExtensionsList") queueExtensionsListRef!: ExtensionsListComponent;
  @ViewChild("loginExtensionsList") loginExtensionsListRef!: ExtensionsListComponent;

  E2eLocators = E2eLocators;

  ringStrategies = ringStrategies;
  ringDurations = ringDurations;
  connectionDelays = connectionDelays;
  wrapupTimes = wrapupTimes;
  announceFrequencies = announceFrequencies;
  callerTimeouts = callerTimeouts;
  reportFrequencies = reportFrequencies;
  frequencyMap = frequencyMap;
  ringStrategyMapRev = ringStrategyMapRev;
  recordingAnnouncement = recordingAnnouncement;

  loadingStateComplete$ = new BehaviorSubject(true);

  isBasic = false;
  isEnhanced = false;
  recordingsArray!: Array<Recording | undefined>;
  views = views;
  showEditPanel = false;
  extensionsList: Array<string> = [];
  totalAddedExtNumAndEmail = 0;
  addedExtNumAndEmailCount = 0;
  domain = "";
  queuePricing!: string;
  userUnlimited = false;
  basicAgentPricing!: string;
  basicAgentPricingText = "";
  basicHeaderAgentPricingText = "";
  enhancedAgentPricing!: string;
  enhancedAgentPricingText = "";
  enhancedHeaderAgentPricingText = "";
  phonesList: Array<string> = [];
  allMusicSources: Array<MusicSource> = [];
  allVoicemailBoxes: Array<AdvVoicemailbox> = [];
  storageServices: Array<StorageService> = [];

  queueInfo = this.fb.group({
    queueName: ["", [Validators.required, Validators.pattern(NAME_VALIDATOR_REGEX)]],
    queueSipAddress: [""],
    loginSipAddress: [""],
    loginPassword: [
      "",
      [
        Validators.required,
        Validators.pattern("^[0-9]*$"),
        Validators.minLength(1),
        Validators.maxLength(8)
      ]
    ],
    loginEmail: [""],
    maxCallers: ["", Validators.required],
    maxAgents: [0, Validators.required],
    queueExtensions: [<Array<string>>[]],
    loginExtensions: [<Array<string>>[]],
    loginPhoneNumbers: [[]],
    ringStrategy: [""],
    ringDuration: [""],
    connectionDelay: [""],
    wrapupTime: [""],
    reportHoldTime: [false],
    announceFrequency: [""],
    musicSource: [<MusicSource | undefined>undefined],
    callerTimeout: [""],
    failoverAddress: [""],
    escapeAddress: [<AdvVoicemailbox | "None">"None"],
    queueReporting: [false],
    callRecording: [false],
    storageService: [<StorageService | undefined>undefined],
    recordingAnnouncement: [this.recordingAnnouncement.none],
    recordingTone: [false],
    emailReports: this.fb.array([])
  });

  isMobile = false;
  smallBreakpoint = false;

  private defaultMusicSource = {
    default: false,
    musicSourceId: "0",
    name: "Ringing only",
    organizationId: "",
    playlist: false,
    url: ""
  };

  private unsubscriber = new Subscription();

  private dialogOptions = {
    panelClass: ["mat-typography", "onsip-dialog-universal-style"],
    restoreFocus: false,
    autoFocus: false
  };

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private snackbar: SnackbarService,
    private resourceService: AccountResourceService,
    private packageService: PackageService,
    private cdRef: ChangeDetectorRef,
    private musicSourceService: MusicSourceService,
    private storageService: StorageServiceService,
    private voicemail: VoicemailService,
    private queueService: AdvQueueService,
    private userService: UserService,
    private userAddress: UserAddressService,
    private emailReportService: AdvQueueEmailReportService,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit() {
    this.setFormValues();
    this.getAgentPricing();
    this.getUserPackage();
    this.getMusicSources();
    this.setBreakpoints();
    this.storageService.storageServiceBrowse();
    this.unsubscriber.add(
      this.userService.orgState.pipe(take(1)).subscribe(users => (this.domain = users[0].domain))
    );
  }

  setQueueType(type: "basic" | "enhanced"): void {
    if (type === "basic") {
      this.isBasic = true;
      this.isEnhanced = false;
    } else {
      this.isBasic = false;
      this.isEnhanced = true;
      this.queueInfo.controls.callRecording.setValue(true);
    }
  }

  setFormValues(): void {
    this.queueInfo.controls.maxCallers.setValue("25");
    this.queueInfo.controls.maxAgents.setValue(5);
    this.queueInfo.controls.ringDuration.setValue("30");
    this.queueInfo.controls.ringStrategy.setValue("Roundrobin memory");
    this.queueInfo.controls.connectionDelay.setValue("0");
    this.queueInfo.controls.reportHoldTime.setValue(false);
    this.queueInfo.controls.wrapupTime.setValue("0");
    this.queueInfo.controls.announceFrequency.setValue("0");
    this.queueInfo.controls.callerTimeout.setValue("0");
    this.queueInfo.controls.queueReporting.setValue(true);
    this.queueInfo.controls.callRecording.setValue(true);
    this.unsubscriber.add(
      this.queueInfo.controls.callRecording.valueChanges.subscribe(value => {
        if (!value) {
          this.queueInfo.controls.storageService.clearValidators();
          this.queueInfo.controls.recordingAnnouncement.clearValidators();
          this.queueInfo.controls.storageService.setValue(undefined);
          this.queueInfo.controls.recordingAnnouncement.setValue(this.recordingAnnouncement.none);
          this.queueInfo.controls.recordingTone.setValue(false);
        } else {
          this.setRecordingFormValues();
        }
        this.queueInfo.updateValueAndValidity({ emitEvent: false });
      })
    );
    this.voicemail.advVoicemailboxBrowseWithOrgId();
    this.unsubscriber.add(
      this.voicemail.orgState.subscribe((voicemails: Array<AdvVoicemailbox>) => {
        this.allVoicemailBoxes = voicemails;
        this.queueInfo.controls.escapeAddress.setValue("None");
      })
    );
    this.unsubscriber.add(
      this.queueInfo.controls.queueReporting.valueChanges.subscribe(value => {
        if (value) {
          if (!this.emailReports.length) {
            this.addEmailReport();
          }
        } else {
          for (let i = 0; i < this.emailReports.length; i++) {
            if (this.emailReports.controls[i].invalid) {
              this.removeEmailReport(i);
            }
          }
        }
      })
    );
  }

  getAgentPricing(): void {
    this.resourceService.accountResourceBrowse();
    this.unsubscriber.add(
      this.resourceService.state
        .pipe(
          filter(state => !state.loading),
          map(state => {
            return Object.values(state.state);
          })
        )
        .pipe(
          map(resourcesArray => {
            return resourcesArray.filter(
              res => res.name === "SimpleQueue" || res.name === "SmartQueue"
            );
          })
        )
        .subscribe(resources => {
          resources.map(resource => {
            if (resource.name === "SimpleQueue") {
              this.basicAgentPricing = resource?.monthlyRecurringCharge || "undetermined";
              this.basicAgentPricingText = `$${
                resource?.monthlyRecurringCharge || "undetermined"
              }/agent`;
              this.basicHeaderAgentPricingText = `Pricing: $${resource?.monthlyRecurringCharge}/month per agent`;
              this.cdRef.markForCheck();
            } else {
              this.enhancedAgentPricing = resource?.monthlyRecurringCharge || "undetermined";
              this.enhancedAgentPricingText = `$${
                resource?.monthlyRecurringCharge || "undetermined"
              }/agent`;
              this.enhancedHeaderAgentPricingText = `Pricing: $${resource?.monthlyRecurringCharge}/month per agent`;
              this.cdRef.markForCheck();
            }
          });
        })
    );
  }

  getUserPackage(): void {
    this.unsubscriber.add(
      this.packageService.getAssignedPrimaryPackages().subscribe(packages => {
        packages.map(pkg => {
          if (pkg.status === "user") {
            this.userUnlimited = true;
          }
        });
        this.cdRef.markForCheck();
      })
    );
    this.unsubscriber.add(
      this.queueInfo.controls.queueName.valueChanges.subscribe(value => {
        if (value) {
          this.queueInfo.controls.queueSipAddress.setValue(value);
          this.queueInfo.controls.loginSipAddress.setValue(value + "_login");
        } else {
          this.queueInfo.controls.queueSipAddress.setValue("");
          this.queueInfo.controls.loginSipAddress.setValue("");
        }
      })
    );
  }

  setBreakpoints(): void {
    this.unsubscriber.add(
      this.breakpointObserver.observe(Breakpoints.XSmall).subscribe(result => {
        this.isMobile = result.matches;
        this.cdRef.markForCheck();
      })
    );

    this.unsubscriber.add(
      this.breakpointObserver.observe(Breakpoints.Small).subscribe(result => {
        this.smallBreakpoint = result.matches;
        this.cdRef.markForCheck();
      })
    );
  }

  getMusicSources(): void {
    this.musicSourceService.musicSourceBrowse();
    this.unsubscriber.add(
      this.musicSourceService.orgState.subscribe(musicSources => {
        this.allMusicSources = [this.defaultMusicSource].concat(musicSources);
        this.queueInfo.controls.musicSource.setValue(
          musicSources.find(musicSource => musicSource.default)
        );
      })
    );
  }

  createMusicSource(): void {
    this.dialog.open(EditMusicSourceDialogComponent, { ...this.dialogOptions });
  }

  getQueueNameError(): string {
    const announcementName = this.queueInfo.controls.queueName;
    return announcementName.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.MUST_BE_LESS_THAN_60_CHARACTERS")
      : announcementName.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
        )
      : announcementName.hasError("required")
      ? this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED")
      : "";
  }

  showErrorSnackbar(message: string): void {
    this.snackbar.openSnackBar(message, "error");
  }

  getVoicemailAddress(addressId: string): string {
    let voicemailAddress = "";
    this.unsubscriber.add(
      this.userAddress.orgState.subscribe((userAddresses: Array<UserAddress>) => {
        const address = userAddresses.find(
          userAddress => userAddress.defaultAddress.addressId === addressId
        );
        voicemailAddress =
          address && address.defaultAddress.username
            ? address.defaultAddress.username + "@" + address.domain
            : "";
      })
    );
    return voicemailAddress;
  }

  setRecordingFormValues(): void {
    this.unsubscriber.add(
      this.storageService.orgState.subscribe(storageServices => {
        this.storageServices = storageServices;
        this.queueInfo.controls.storageService.setValue(storageServices[0]);
      })
    );
    this.queueInfo.controls.recordingAnnouncement.setValue(this.recordingAnnouncement.none);
    this.queueInfo.controls.recordingTone.setValue(false);
  }

  addAddedEmailReports(queueId: string): void {
    let addCounter = 0;
    this.emailReportsAsArray.map(report => {
      const addParams = {
        AdvQueueId: queueId,
        ToAddress: report.email,
        Frequency: this.frequencyMap[report.frequency]
      };
      this.emailReportService.advQueueEmailReportAdd(addParams).then(result => {
        if (result.status === "success") {
          addCounter += 1;
          if (addCounter === this.emailReportsAsArray.length) {
            this.showSaveSnackbar();
            this.queueService.advQueueBrowse();
          }
        } else {
          this.showErrorSnackbar(result.data.message);
          this.emailReportService.clearErrors();
        }
      });
    });
  }

  addStorageService(): void {
    this.dialog.open(AddStorageServiceDialogComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"]
    });
  }

  handleCancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: "Cancel setup?",
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.router.navigate([views.ADMIN_ACD_QUEUES]);
          }
        })
    );
  }

  nextStep(curStep?: "basic" | "caller" | "enhanced" | "agent"): void {
    this.queueInfo.markAllAsTouched();
    if (this.queueInfo.valid) {
      if (curStep === "basic") {
        this.unsubscriber.add(
          this.dialog
            .open(ModalMaterialComponent, {
              panelClass: ["mat-typography", "onsip-dialog-universal-style"],
              data: {
                title: `Are you sure you want to add ${this.queueInfo.controls.maxAgents.value} agents?`,
                message: this.isBasic
                  ? `You are adding a maximum number of ${
                      this.queueInfo.controls.maxAgents.value
                    } agents to your queue. Your account will be charged $${(
                      +this.queueInfo.controls.maxAgents.value * +this.basicAgentPricing
                    )
                      .toFixed(2)
                      .toString()}/month + taxes & fees.`
                  : `You are adding a maximum number of ${
                      this.queueInfo.controls.maxAgents.value
                    } agents to your queue. Your account will be charged $${(
                      +this.queueInfo.controls.maxAgents.value * +this.enhancedAgentPricing
                    )
                      .toFixed(2)
                      .toString()}/month + taxes & fees.`,
                primaryBtnText: "Add agents",
                secondaryBtnText: "Cancel",
                primaryBtnFlat: true
              }
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(retObj => {
              if (retObj && retObj.doPrimaryAction) {
                if (this.stepper.selected) this.stepper.selected.completed = true;
                this.stepper.next();
              }
            })
        );
      } else {
        if (this.stepper.selected) this.stepper.selected.completed = true;
        this.stepper.next();
        if (curStep === "caller" && this.isEnhanced) {
          if (!this.emailReports.length && this.queueInfo.controls.queueReporting.value) {
            this.addEmailReport();
          }
        }
        if (curStep === "enhanced") {
          if (!this.queueInfo.controls.queueReporting.value) {
            return;
          }
        }
      }
    }
  }

  backStep(isReview = false): void {
    if (this.queueInfo.valid) {
      if (isReview) {
        this.showEditPanel = false;
      }
      this.stepper.previous();
    } else {
      this.queueInfo.markAllAsTouched();
      this.queueInfo.updateValueAndValidity();
    }
  }

  toggleEditPanel(isCancel = false): void {
    if (!this.showEditPanel) {
      this.showEditPanel = !this.showEditPanel;
    } else {
      if (isCancel) {
        this.unsubscriber.add(
          this.dialog
            .open(ModalMaterialComponent, {
              panelClass: ["mat-typography", "onsip-dialog-universal-style"],
              data: {
                title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
                message: this.translate.instant(
                  "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
                ),
                primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
                secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
                primaryBtnFlat: true
              }
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(retObj => {
              if (retObj && retObj.doPrimaryAction) {
                this.showEditPanel = !this.showEditPanel;
                this.cdRef.markForCheck();
              }
            })
        );
      } else {
        this.showEditPanel = !this.showEditPanel;
      }
    }
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  showSaveSnackbar(): void {
    this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
  }

  submit(): void {
    this.loadingStateComplete$.next(false);
    this.queueService
      .advQueueAdd({
        QueueName: this.queueInfo.controls.queueName.value,
        MaxCaller: this.queueInfo.controls.maxCallers.value,
        MaxAgent: this.queueInfo.controls.maxAgents.value,
        CallerTimeout: this.queueInfo.controls.callerTimeout.value,
        AgentTimeout: this.queueInfo.controls.ringDuration.value,
        RingStrategy: this.ringStrategyMapRev[this.queueInfo.controls.ringStrategy.value],
        WrapupTime: this.queueInfo.controls.wrapupTime.value,
        MemberDelay: this.queueInfo.controls.connectionDelay.value,
        AnnounceFrequency:
          this.queueInfo.controls.announceFrequency.value === "0"
            ? "30"
            : this.queueInfo.controls.announceFrequency.value,
        Username: this.queueInfo.controls.loginSipAddress.value.substring(
          0,
          this.queueInfo.controls.loginSipAddress.value.length - 6
        ),
        Password: this.queueInfo.controls.loginPassword.value,
        SmartQueue: this.isEnhanced,
        AnnounceHoldTime: this.queueInfo.controls.announceFrequency.value === "0" ? false : true,
        ReportHoldTime: this.queueInfo.controls.reportHoldTime.value,
        EscapeAddress:
          this.queueInfo.controls.escapeAddress.value &&
          this.queueInfo.controls.escapeAddress.value !== "None"
            ? this.getVoicemailAddress(this.queueInfo.controls.escapeAddress.value.addressId)
            : "",
        EscapeAddressId:
          this.queueInfo.controls.escapeAddress.value &&
          this.queueInfo.controls.escapeAddress.value !== "None"
            ? this.queueInfo.controls.escapeAddress.value.addressId
            : "0",
        FailoverAddress: this.queueInfo.controls.failoverAddress.value,
        RingOnly: this.queueInfo.controls.musicSource.value?.musicSourceId === "0",
        MusicSourceId: this.queueInfo.controls.musicSource.value?.musicSourceId,
        MusicSourceName: this.queueInfo.controls.musicSource.value?.name,
        MusicSourceUrl: this.queueInfo.controls.musicSource.value?.url,
        StorageServiceId: this.queueInfo.controls.callRecording.value
          ? this.queueInfo.controls.storageService.value?.storageServiceId
          : "0",
        StorageServiceName: this.queueInfo.controls.callRecording.value
          ? this.queueInfo.controls.storageService.value?.name
          : "",
        StorageServiceService: this.queueInfo.controls.callRecording.value
          ? this.queueInfo.controls.storageService.value?.service
          : "",
        AnnounceRecording: this.queueInfo.controls.callRecording.value
          ? this.queueInfo.controls.recordingAnnouncement.value
          : "",
        RecordingTone: this.queueInfo.controls.recordingTone.value
      })
      .then(result => {
        if (result.status === "success") {
          if (this.queueInfo.controls.queueExtensions.value.length > 0) {
            this.queueExtensionsListRef.editExtensions(
              this.queueInfo.controls.queueSipAddress.value
            );
          }
          if (this.queueInfo.controls.loginExtensions.value.length > 0) {
            this.loginExtensionsListRef.editExtensions(
              this.queueInfo.controls.loginSipAddress.value
            );
          }
          if (this.queueInfo.controls.queueReporting.value && this.emailReports.length > 0) {
            const queueId = Object.values(result.data)[0].advQueueId;
            this.addAddedEmailReports(queueId);
          } else {
            this.showSaveSnackbar();
          }
          this.userService.userRead();
          this.router.navigate([views.ADMIN_ACD_QUEUES]);
        } else {
          this.showErrorSnackbar(result.data.message);
          this.queueService.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
  }

  getGenericError(control: AbstractControl, value: string, isLoginPasswordControl = false): string {
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : control.hasError("pattern") && isLoginPasswordControl
      ? this.translate.instant("ONSIP_I18N.ACD_QUEUE_LOGIN_PASSWORD")
      : control.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.VALUE_IS_LONGER_THAN_LENGTH_CHARACTERS", {
          value,
          length: control.errors?.maxlength.requiredLength
        })
      : control.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N._VALUE__CAN_ONLY_CONTAIN_LETTERS_SPACES_QUOTES_OR_DASHES",
          { value }
        )
      : control.hasError("email")
      ? this.translate.instant("ONSIP_I18N.EMAIL_ADDRESS_IS_NOT_VALID", { value })
      : "";
  }

  getGenericRequiredError(control: AbstractControl | undefined | null, value: string): string {
    // eslint-disable-next-line no-null/no-null
    if (control === undefined || control === null) return "";
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : "";
  }

  get emailReports(): FormArray {
    return this.queueInfo.get("emailReports") as FormArray;
  }

  get emailReportsAsArray(): Array<EmailReport> {
    return this.queueInfo.get("emailReports")?.value as Array<EmailReport>;
  }

  addEmailReport(): void {
    this.emailReports.push(
      new FormGroup({
        frequency: new FormControl(this.reportFrequencies[0], [Validators.required]),
        email: new FormControl("", [Validators.required, Validators.email])
      })
    );
  }

  removeEmailReport(index: number): void {
    this.emailReports.removeAt(index);
  }
}
